import Config from "../_config/config";
import myAxios from "./axiositeachWrapper";

const BASE = `${Config.nodeSiteUrl}v1/enrollment`;

class EnrollmentService {
  /** Aggregated enrollment details (sidebar + identity + academic + course) */
  getDetails = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/details`);

  /** Active + deferred modules */
  getModules = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/modules`);

  /** Installment plan, payment details, discounts */
  getPlanAndPayments = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/plan-and-payments`);

  /** Enrollment documents */
  getDocuments = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/documents`);

  /** Status change audit trail */
  getStatusHistory = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/status-history`);

  /** Credit notes */
  getCreditNotes = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/credit-notes`);

  /** Refund advice + refund details */
  getRefunds = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/refunds`);

  /** Outstanding amounts per installment head */
  getOutstanding = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/outstanding`);

  /** Reconcile: installment templates eligible for the enrollment */
  getReconcileTemplates = (filter: any) =>
    myAxios.post<any>(`${BASE}/installment-templates`, filter);

  /** Reconcile: preview the plan a template would produce (read-only) */
  getPreplan = (enrollmentId: number, templateId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/preplan/${templateId}`);

  /** Enrollment-independent template plan preview (New Enrollment form) */
  getTemplatePreview = (templateId: number) =>
    myAxios.get<any>(`${BASE}/template-preview/${templateId}`);

  /** Reconcile: rebuild the enrollment's plan from a template */
  reconcilePlan = (enrollmentId: number, body: any) =>
    myAxios.post<any>(`${BASE}/${enrollmentId}/reconcile`, body);

  /** Available instrument types for enrollment payment */
  getInstrumentTypes = () =>
    myAxios.get<any>(`${BASE}/instrument-types`);

  /** Cancellation/reversal reasons */
  getCancellationReasons = () =>
    myAxios.get<any>(`${BASE}/cancellation-reasons`);

  /** Reasons selectable when changing enrollment status */
  getStatusChangeReasons = () =>
    myAxios.get<any>(`${BASE}/status-change-reasons`);

  /** Bulk apply an enrollment status change across many enrollments */
  bulkUpdateStatus = (params: {
    enrollmentIds: number[];
    statusId: number;
    reasonId?: number | null;
    remarks?: string | null;
    statusChangeDate?: string | null;
  }) => myAxios.post<any>(`${BASE}/bulk-update-status`, params);

  /** Bulk promote enrollments into a target batch/year (creates new enrollments) */
  bulkPromote = (params: {
    targetBatchId: number;
    targetYearId?: number | null;
    schoolId?: number | null;
    programId?: number | null;
    courseIds?: number[];
    installmentTemplateId?: number | null;
    newEnrollmentStatusId?: number | null;
    studentEnrollmentCategoryId?: number | null;
    studentEnrollmentCategory2Id?: number | null;
    carryForwardTags?: boolean;
    promotionType?: "promote" | "addAnotherCourse";
    remarks?: string | null;
    students: Array<{ enrollmentId: number; studentCode?: string | null }>;
  }) => myAxios.post<any>(`${BASE}/bulk-promote`, params);

  /** Bulk assign a tag (of a tag type) to many enrollments */
  bulkAssignTags = (params: {
    enrollmentIds: number[];
    tagTypeId: number;
    tagId: number;
    taggedDate?: string | null;
    remarks?: string | null;
    replaceSameType?: boolean;
  }) => myAxios.post<any>(`${BASE}/bulk-assign-tags`, params);

  /** Current active tag (of a type) + last tagged date per enrollment */
  getExistingTags = (params: { enrollmentIds: number[]; tagTypeId: number }) =>
    myAxios.post<any>(`${BASE}/existing-tags`, params);

  /** Bulk assign a transport plan (installment template) per enrollment */
  bulkUpdateTransportPlan = (params: {
    assignments: Array<{ enrollmentId: number; templateId: number }>;
  }) => myAxios.post<any>(`${BASE}/bulk-update-transport-plan`, params);

  /** Bulk assign a mentor per enrollment (or one mentor for many, legacy form) */
  bulkAssignMentor = (params: {
    assignments?: Array<{ enrollmentId: number; userId: number }>;
    enrollmentIds?: number[];
    userId?: number;
  }) => myAxios.post<any>(`${BASE}/bulk-assign-mentor`, params);

  /** Bulk assign a staff user (for a role) to many enrollments' enquiries */
  bulkAssignStaff = (params: {
    enrollmentIds: number[];
    userId: number;
    roleId: number;
    remarks?: string | null;
  }) => myAxios.post<any>(`${BASE}/bulk-assign-staff`, params);

  /** Bulk add many enrollments to a sub-batch (additive/attach semantics) */
  bulkAddSubBatch = (params: {
    enrollmentIds: number[];
    subBatchId: number;
    date?: string | null;
    remarks?: string | null;
  }) => myAxios.post<any>(`${BASE}/bulk-add-sub-batch`, params);

  /** Bulk generate ID cards (combined PDF) → returns a pre-signed download URL */
  bulkIdCard = (params: { enrollmentIds: number[] }) =>
    myAxios.post<any>(`${BASE}/bulk-id-card`, params);

  /** Bulk generate grade reports (combined PDF) → returns a pre-signed download URL */
  bulkGradeReport = (params: { enrollmentIds: number[] }) =>
    myAxios.post<any>(`${BASE}/bulk-grade-report`, params);

  /** Bulk issue transfer certificates (audit-only status record) */
  bulkIssueTc = (params: {
    enrollmentIds: number[];
    statusId: number;
    tcStatusId: number;
    tcReasonId: number;
    date?: string | null;
    remarks: string;
  }) => myAxios.post<any>(`${BASE}/bulk-issue-tc`, params);

  /** Bulk download selected students' photos as a ZIP (returns a blob) */
  bulkStudentPhotos = (params: { enrollmentIds: number[] }) =>
    myAxios.post<any>(`${BASE}/bulk-student-photos`, params, { responseType: "blob" });

  /** Preload existing health details for the selected enrollments (grid pre-fill) */
  getHealthDetails = (enrollmentIds: number[]) =>
    myAxios.get<any>(`${BASE}/health-details`, {
      params: { enrollmentIds: enrollmentIds.join(",") },
    });

  /** Bulk save health details for the selected enrollments */
  bulkHealthDetails = (params: {
    consultationDate: string;
    records: Array<Record<string, any>>;
  }) => myAxios.post<any>(`${BASE}/bulk-health-details`, params);

  /** "Add Demand" dropdown: fee heads attached to a miscellaneous installment */
  getDemandHeads = () => myAxios.get<any>(`${BASE}/demand-heads`);

  /** Bulk add a fee demand charge to the selected enrollments */
  bulkAddDemand = (params: {
    enrollmentIds: number[];
    installmentHeadId: number;
    amount: number;
    remarks?: string | null;
  }) => myAxios.post<any>(`${BASE}/bulk-add-demand`, params);

  /** Issue Material header dropdowns (godowns, years, users, courses) */
  getIssueMaterialLookups = () => myAxios.get<any>(`${BASE}/issue-material-lookups`);

  /** Issue Material cascade: assemblies for a course + year */
  getMaterialAssemblies = (courseId: number, yearId: number) =>
    myAxios.get<any>(`${BASE}/material-assemblies`, { params: { courseId, yearId } });

  /** Issue Material cascade: components under an assembly */
  getMaterialComponents = (assemblyId: number) =>
    myAxios.get<any>(`${BASE}/material-components`, { params: { assemblyId } });

  /** "Head" dropdown for Bulk Discount: discount-eligible (non-tax) installment heads */
  getDiscountHeads = () => myAxios.get<any>(`${BASE}/discount-heads`);

  /** "Pass Out Year" dropdown for Transfer to Alumni */
  getPassoutYears = () => myAxios.get<any>(`${BASE}/passout-years`);

  /** Bulk transfer selected students to alumni records */
  bulkTransferToAlumni = (params: {
    enrollmentIds: number[];
    passOutYearId: number;
    remarks?: string | null;
  }) => myAxios.post<any>(`${BASE}/bulk-transfer-alumni`, params);

  /** Bulk apply discounts (one row per enrollment) */
  bulkDiscount = (params: {
    rows: Array<{
      enrollmentId: number;
      discountCategoryId: number;
      installmentHeadId: number;
      amount: number;
      discountType: number;
      remarks?: string | null;
    }>;
  }) => myAxios.post<any>(`${BASE}/bulk-discount`, params);

  /** Bulk issue materials to the selected enrollments (stock movement) */
  bulkIssueMaterial = (params: {
    enrollmentIds: number[];
    indentNumber: string;
    shipmentDate: string;
    fromGodownId: number;
    yearId?: number | null;
    raisedByUserId?: number | null;
    remarks?: string | null;
    lines: Array<{
      courseId?: number | null;
      assemblyId: number;
      componentId?: number | null;
      quantity: number;
      discount?: number;
    }>;
  }) => myAxios.post<any>(`${BASE}/bulk-issue-material`, params);

  /** Bulk change the placement status of many enrollments */
  bulkChangePlacementStatus = (params: {
    enrollmentIds: number[];
    placementStatusId: number;
  }) => myAxios.post<any>(`${BASE}/bulk-change-placement-status`, params);

  /** Bulk assign a stream (+ its non-elective courses) to many enrollments */
  bulkAssignStreams = (params: {
    enrollmentIds: number[];
    streamId: number;
  }) => myAxios.post<any>(`${BASE}/bulk-assign-streams`, params);

  /** Bulk assign a course (module or elective) to many enrollments */
  bulkAssignCourse = (params: {
    enrollmentIds: number[];
    courseId: number;
    termId?: number | null;
    remarks?: string | null;
  }) => myAxios.post<any>(`${BASE}/bulk-assign-course`, params);

  /** Bulk drop a course/module from many enrollments */
  bulkDropModules = (params: {
    enrollmentIds: number[];
    courseId: number;
    dropStatusId: number;
    remarks?: string | null;
  }) => myAxios.post<any>(`${BASE}/bulk-drop-modules`, params);

  /** Bulk transfer (move) many enrollments to a target batch */
  bulkTransfer = (params: {
    enrollmentIds: number[];
    batchType?: number;
    targetBatchId?: number;
    subBatchId?: number | null;
    targetYearId?: number | null;
    deliveryFormatId?: number | null;
    transferDate?: string | null;
    reasonForTransfer?: string | null;
    remarks?: string | null;
  }) => myAxios.post<any>(`${BASE}/bulk-transfer`, params);

  /** Discount categories */
  getDiscountCategories = () =>
    myAxios.get<any>(`${BASE}/discount-categories`);

  /** Everything the Apply Discount dialog needs, personalised for the current user */
  getDiscountContext = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/discount-context`);

  /** Discount approval queue */
  getDiscountApprovals = () =>
    myAxios.get<any>(`${BASE}/discount-approvals`);

  actOnDiscountApproval = (approvalId: number, actionType: 0 | 1) =>
    myAxios.post<any>(`${BASE}/discount-approvals/${approvalId}/action`, { actionType });

  applyDiscountApproval = (approvalId: number) =>
    myAxios.post<any>(`${BASE}/discount-approvals/${approvalId}/apply`);

  /** Receipt data for a payment */
  getReceipt = (paymentId: number) =>
    myAxios.get<any>(`${BASE}/payment/${paymentId}/receipt`);

  /** Printable Statement of Account HTML */
  getSOA = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/statement-of-account/print`);

  /** Printable Reminder Slip HTML (outstanding dues as of a date) */
  getReminderSlip = (enrollmentId: number, asOfDate?: string) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/reminder-slip/print`, {
      params: asOfDate ? { asOfDate } : {},
    });

  /** Enrollments for a student (by enquiry ID) */
  getEnrollmentsByEnquiry = (enquiryId: number) =>
    myAxios.get<any>(`${BASE}/by-enquiry/${enquiryId}`);

  /** Collect payment */
  collectPayment = (enrollmentId: number, body: any) =>
    myAxios.post<any>(`${BASE}/${enrollmentId}/collect-payment`, body);

  /** Reverse payment */
  reversePayment = (enrollmentId: number, body: any) =>
    myAxios.post<any>(`${BASE}/${enrollmentId}/reverse-payment`, body);

  /** Apply discount */
  applyDiscount = (enrollmentId: number, body: any) =>
    myAxios.post<any>(`${BASE}/${enrollmentId}/apply-discount`, body);

  /** Reverse a previously applied discount */
  reverseDiscount = (enrollmentId: number, body: any) =>
    myAxios.post<any>(`${BASE}/${enrollmentId}/reverse-discount`, body);

  /** Payment follow-up remarks */
  getPaymentFollowups = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/payment-followups`);

  /** Add payment follow-up remark */
  addPaymentFollowup = (enrollmentId: number, body: any) =>
    myAxios.post<any>(`${BASE}/${enrollmentId}/add-payment-followup`, body);

  /** Upload enrollment document */
  uploadDocument = (enrollmentId: number, formData: FormData) =>
    myAxios.post<any>(`${BASE}/${enrollmentId}/upload-document`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

  /** Credit account balance + transaction ledger */
  getCreditAccount = (enrollmentId: number) =>
    myAxios.get<any>(`${BASE}/${enrollmentId}/credit-account`);

  /** Credit type dropdown data */
  getCreditTypes = () =>
    myAxios.get<any>(`${BASE}/credit-types`);

  /** Add credit manually */
  addCredit = (enrollmentId: number, body: any) =>
    myAxios.post<any>(`${BASE}/${enrollmentId}/add-credit`, body);

  /** Adjust credit against outstanding installments */
  adjustCredit = (enrollmentId: number, body: any) =>
    myAxios.post<any>(`${BASE}/${enrollmentId}/adjust-credit`, body);

  /** Refund credit to student */
  refundCredit = (enrollmentId: number, body: any) =>
    myAxios.post<any>(`${BASE}/${enrollmentId}/refund-credit`, body);

  /** Generate invoice(s) for an enrollment */
  generateInvoice = (enrollmentId: number, body: any) =>
    myAxios.post<any>(`${BASE}/${enrollmentId}/generate-invoice`, body);

  /** Generate PDF for an invoice and upload to S3 */
  generateInvoicePdf = (invoiceId: number) =>
    myAxios.post<any>(`${BASE}/invoice/${invoiceId}/generate-pdf`);

  /** List invoice generation runs */
  getInvoiceRuns = () =>
    myAxios.get<any>(`${BASE}/invoice-runs`);

  /** Trigger bulk invoice generation run */
  bulkInvoiceRun = (body: any) =>
    myAxios.post<any>(`${BASE}/invoice-run`, body);

  /** Lookup data for enrollment create/edit form */
  getLookups = () =>
    myAxios.get<any>(`${BASE}/lookups`);

  /** Update an existing enrollment */
  updateEnrollment = (enrollmentId: number, body: any) =>
    myAxios.put<any>(`${BASE}/${enrollmentId}`, body);

  /** Create a new enrollment */
  createEnrollment = (body: any) =>
    myAxios.post<any>(`${BASE}`, body);

  /** Defaults for the New Enrollment form, derived from the source enquiry
   *  (schoolId ← partner, yearId, courseId, programId) */
  getEnquiryDefaults = (enquiryId: number) =>
    myAxios.get<any>(`${BASE}/enquiry-defaults/${enquiryId}`);
}

export default new EnrollmentService();
